import type { PointerEvent as ReactPointerEvent } from 'react'

/**
 * Drags an edge from a resize handle's `pointerdown`, reporting the new width
 * as the pointer moves and calling `onEnd` once the drag ends. Marks the
 * handle `data-active` while dragging.
 */
export function dragWidth(
  e: ReactPointerEvent<HTMLElement>,
  startWidth: number,
  onResize: (width: number) => void,
  onEnd: () => void,
) {
  if (e.button !== 0) {
    return
  }
  // Keeps the drag from selecting text.
  e.preventDefault()
  const handle = e.currentTarget
  const startX = e.clientX
  handle.setPointerCapture(e.pointerId)
  handle.dataset.active = 'true'

  const move = (ev: PointerEvent) => onResize(startWidth + ev.clientX - startX)
  const end = () => {
    delete handle.dataset.active
    handle.removeEventListener('pointermove', move)
    handle.removeEventListener('lostpointercapture', end)
    onEnd()
  }
  handle.addEventListener('pointermove', move)
  handle.addEventListener('lostpointercapture', end)
}

export interface PressDragHandlers {
  onMove: (e: PointerEvent) => void
  onEnd: () => void
  onCancel: () => void
}

/**
 * Follows a press from `pointerdown` that turns into a drag once the pointer
 * moves past `threshold` pixels, so a press that stays put remains a click.
 * Escape cancels the drag. The click that ends a drag is swallowed.
 */
export function dragFromPress(
  e: ReactPointerEvent<HTMLElement>,
  { onMove, onEnd, onCancel }: PressDragHandlers,
  threshold = 4,
) {
  if (e.button !== 0) {
    return
  }
  // Keeps the drag from selecting text.
  e.preventDefault()
  const startX = e.clientX
  const startY = e.clientY
  let state: 'pressed' | 'dragging' | 'cancelled' = 'pressed'

  const move = (ev: PointerEvent) => {
    if (state === 'pressed') {
      if (
        Math.abs(ev.clientX - startX) <= threshold &&
        Math.abs(ev.clientY - startY) <= threshold
      ) {
        return
      }
      state = 'dragging'
    }
    if (state === 'dragging') {
      onMove(ev)
    }
  }
  const keyDown = (ev: KeyboardEvent) => {
    if (ev.key !== 'Escape' || state !== 'dragging') {
      return
    }
    ev.preventDefault()
    ev.stopPropagation()
    state = 'cancelled'
    onCancel()
  }
  const unbind = () => {
    window.removeEventListener('pointermove', move)
    window.removeEventListener('pointerup', up)
    window.removeEventListener('pointercancel', cancel)
    window.removeEventListener('keydown', keyDown, true)
  }
  const up = () => {
    unbind()
    if (state === 'pressed') {
      return
    }
    swallowNextClick()
    if (state === 'dragging') {
      onEnd()
    }
  }
  const cancel = () => {
    unbind()
    if (state === 'dragging') {
      onCancel()
    }
  }
  window.addEventListener('pointermove', move)
  window.addEventListener('pointerup', up)
  window.addEventListener('pointercancel', cancel)
  window.addEventListener('keydown', keyDown, true)
}

function swallowNextClick() {
  const swallow = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
  }
  window.addEventListener('click', swallow, { capture: true, once: true })
  // The click follows `pointerup` synchronously, when there is one at all.
  setTimeout(() => window.removeEventListener('click', swallow, true))
}
